// Sentry initialisation for the client bundle. Catches unhandled errors,
// React component crashes (via the ErrorBoundary export below), and unhandled
// promise rejections. Gated on `VITE_SENTRY_DSN` so the absence of the env
// var is a clean no-op (no console noise, no broken behavior).
//
// We deliberately skip Session Replay and full Performance Monitoring — both
// add bundle weight and burn through Sentry's free quota fast. Error capture
// alone is enough to catch the things that actually hurt customers.

import * as Sentry from "@sentry/react";

let initialized = false;

export const initSentry = () => {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // feature disabled — skip silently
  initialized = true;

  Sentry.init({
    dsn,
    // "production" if running on the prod domain, "preview" for Vercel previews
    // (still useful — preview crashes are worth catching), "development" locally.
    environment: import.meta.env.MODE === "production"
      ? (window.location.hostname === "mediaonautopilot.com" ? "production" : "preview")
      : "development",
    // Surface the commit SHA in Sentry so each event ties back to a deploy.
    // Vercel sets VITE_VERCEL_GIT_COMMIT_SHA automatically on every build.
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || undefined,
    // We're not turning on Performance Monitoring (transactions) right now;
    // 0 means no tracing data sent. If you want it later, bump to 0.1 (10%).
    tracesSampleRate: 0,
    // Same — Session Replay is off. Tracks user interactions before the crash
    // for debugging, but expensive in events. Worth enabling if a specific
    // bug becomes hard to reproduce.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Don't send events from the Vite dev server. Local stack traces aren't
    // useful and they burn quota.
    enabled: import.meta.env.MODE !== "development" || !!import.meta.env.VITE_SENTRY_DEBUG,
    // Strip a few noisy errors that aren't actionable — extension scripts
    // injecting code, ResizeObserver loop notifications, etc.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
  });
};

// Re-exported so callers can wrap React subtrees with <SentryErrorBoundary>.
// Used in App.jsx around the root to catch component-render crashes.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export { Sentry };
